import React, { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authFetch, httpClient } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog";
import { showStatusToast } from "../../components/ui/Toast";
import UserSearchTagPicker from "../../components/ui/UserSearchTagPicker";
import { useEtatGlobal } from "../EtatGlobal.js";
import { useClients } from "../Portefeuille/useClients.js";
import { normalizeText } from "../../utils/text.js";
import { uploadFileToBlob } from "../../utils/uploadFileToBlob";
import {
  FileText,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Loader2,
  Check,
  CalendarDays,
  Bell,
  X,
  Plus,
  Building2,
  User,
} from "lucide-react";

const DOCUMENT_SLOTS = [
  "bilanDocument",
  "identityDocument",
  "kbisDocument",
  "statusDocument",
  "pieceIdentite",
];

// Ordre de repli pour déterminer "le" collaborateur d'un client — même
// ordre que CLIENT_OWNER_FIELDS (Bureau.jsx) : Expert-comptable, puis
// Superviseur, puis Collaborateur, etc. Utilisé pour pré-remplir "Personne
// chez Alfa" (Demande de rdv) et la liste des collaborateurs à assigner
// (autres catégories) à partir de la fiche du client sélectionné.
const CLIENT_COLLAB_FIELDS = [
  "id_expertComptable",
  "id_superviseur",
  "id_collaborateur",
  "id_collaborateur_niv2",
  "id_social",
  "id_juridique",
];

// Catégories "génériques" pour lesquelles on assigne automatiquement les
// collaborateurs existants du client via "Créer une tâche, notifier".
const CATEGORIES_ASSIGNATION_AUTO = ["Compta / fiscal", "Social", "Juridique", "Autre"];

// Même dérivation de statut que le backend (voir toDto/deriveStatut dans
// traitementsComptablesController.js) — reproduite ici pour construire un
// ticket optimiste identique à ce que renverrait un GET, sans attendre de
// refetch réseau.
function deriveStatutLocal(etape, etapePrecedente) {
  if (etape.niveau > 1 && !etapePrecedente?.closedAt) return "VERROUILLE";
  if (etape.closedAt) return "CLOTURE";
  if (etape.assigneeId) return "EN_COURS";
  return "A_AFFECTER";
}

// Construit, à partir de la réponse de POST /api/evenements (qui inclut déjà
// le ticket créé et ses 3 étapes, voir createEvenement côté backend), la même
// forme de ticket que celle utilisée par SuiviTraitementsComptables.js — pour
// l'ajouter instantanément à traitementsComptablesData sans attendre le
// fetchTraitementsComptables de fond.
function buildOptimisticTicket(evenement, allUsers, clientNom) {
  const ticket = evenement?.pieceComptableTicket;
  if (!ticket) return null;
  const userMap = {};
  allUsers.forEach((u) => {
    userMap[u.id] = u;
  });
  const etapesTriees = [...ticket.etapes].sort((a, b) => a.niveau - b.niveau);
  return {
    id: ticket.id,
    client: { id: evenement.clientId, nom: clientNom },
    dateArrivee: evenement.createdAt,
    naturePieces: evenement.descriptif || "",
    periode: evenement.periodeConcernee || "",
    etapes: etapesTriees.map((etape, index) => {
      const assigneeUser = etape.assigneeId ? userMap[etape.assigneeId] : null;
      return {
        id: etape.id,
        niveau: etape.niveau,
        statut: deriveStatutLocal(etape, etapesTriees[index - 1]),
        assignee: assigneeUser
          ? {
              id: assigneeUser.id,
              name: assigneeUser.name,
              lastName: assigneeUser.lastName,
              poste: assigneeUser.poste,
            }
          : null,
        dueDate: etape.dueDate,
        closedAt: etape.closedAt,
        closedBy: null,
        clotureATemps: etape.clotureATemps,
        delaiJours: etape.delaiJours,
        enRetardLive:
          !etape.closedAt && !!etape.dueDate && new Date(etape.dueDate) < new Date(),
      };
    }),
  };
}

// Depuis un mail (Mails.jsx, bouton "En tâche") : { clientId, nomClient,
// note, descriptif, type } — pré-remplit le formulaire à l'ouverture, tout
// reste ensuite librement modifiable (recherche client comprise).
function Evenement({ onCancel, prefill }) {
  const {
    fetchTraitementsComptables,
    fetchSuiviCompta,
    setTraitementsComptablesData,
    patchSuiviComptaTacheStatut,
    leads,
    setLeads,
  } = useEtatGlobal();
  const { data: clients = [] } = useClients();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState({
    bilanDocument: null,
    identityDocument: null,
    kbisDocument: null,
    statusDocument: null,
    pieceIdentite: null,
  });

  const [formData, setFormData] = useState(() => ({
    formLeadId: "",
    clientId: prefill?.clientId ? String(prefill.clientId) : "",
    note: prefill?.note || "",
    descriptif: prefill?.descriptif || "",
    categorie: "",
    type: prefill?.type || "",
    typeDate: "",
    tel: "",
    email: "",
    sms: "",
    acceuil: "",
    dateEcheance: "",
    dateRdv: "",
    heureRdv: "",
    siteRdv: "",
    personneAlfaId: "",
    userIds: [], // tableau d'ids utilisateurs
    userEmails: [], // <== ajouté ici
    nomCompletLead: "", // <-- AJOUTÉ ICI
    nomClient: prefill?.nomClient || "", // Nom du client
    periodeArriveePC: "", // Période (catégorie "Arrivée pièces comptables"), texte libre distinct de typeDate
  }));

  const [allUsers, setAllUsers] = useState([]);
  const [userMap, setUserMap] = useState({});

  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [searchClient, setSearchClient] = useState(() => prefill?.nomClient || "");
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null); // { type: "client"|"prospect", ... } — pour la carte verrouillée
  const [isClientLocked, setIsClientLocked] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(() => (prefill?.clientId ? String(prefill.clientId) : ""));
  const [selectedClientEmail, setSelectedClientEmail] = useState("");
  const [clientEmailInput, setClientEmailInput] = useState("");
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [ChaqueAssocieDocument, setChaqueAssocieDocument] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const fileInputRef = useRef(null);

  const [searchUser, setSearchUser] = useState("");
  const [userSuggestions, setUserSuggestions] = useState([]);

  const handleSearchUser = (e) => {
    const q = e.target.value;
    setSearchUser(q);

    if (q.length < 2) {
      setUserSuggestions([]);
      return;
    }

    const filtered = allUsers.filter(
      (user) =>
        normalizeText(`${user.name} ${user.lastName}`).includes(
          normalizeText(q),
        ) && !formData.userIds.includes(user.id),
    );

    setUserSuggestions(filtered);
  };

  const selectUser = (user) => {
    if (!formData.userIds.includes(user.id)) {
      setFormData((prev) => ({
        ...prev,
        userIds: [...prev.userIds, user.id],
      }));
    }
    setSearchUser("");
    setUserSuggestions([]);
  };

  const [addToTodo, setAddToTodo] = useState(false);

  // "Notifier une ou plusieurs personnes" — commun à toutes les catégories
  // (case "Notifier à une autre personne" pour les tâches, "Notifier" pour
  // l'arrivée de pièces comptables). Cochée par défaut (contrairement à
  // "Ajouter à la liste des tâches") — au moins un destinataire reste
  // obligatoire pour valider (voir isFormInvalid).
  const [notifyChecked, setNotifyChecked] = useState(true);
  const [notifyUserIds, setNotifyUserIds] = useState([]);

  // Recherche unifiée Client + Prospect, même principe que
  // SignModif/clientSearch.js : filtrage en mémoire (clients et leads déjà
  // préchargés globalement — useClients()/useEtatGlobal()), debounce 300ms,
  // au moins 2 caractères, pas d'appel réseau à chaque frappe.
  useEffect(() => {
    if (isClientLocked) return;
    const q = searchClient;
    if (q.trim().length < 2) {
      setClientSuggestions([]);
      return;
    }

    const handler = setTimeout(() => {
      const terms = q.toLowerCase().split(" ").filter(Boolean);

      const clientMatches = clients
        .filter((c) => terms.every((term) => (c.nom || "").toLowerCase().includes(term)))
        .slice(0, 25)
        .map((c) => ({ ...c, type: "client" }));

      const leadMatches = (leads || [])
        .filter((l) => l.specificite !== "archive")
        .filter((l) => {
          const fullName = `${l.nom || ""} ${l.prenom || ""}`.toLowerCase();
          return terms.every((term) => fullName.includes(term));
        })
        .map((l) => ({ ...l, type: "prospect", displayName: `${l.prenom || ""} ${l.nom || ""}`.trim() }));

      setClientSuggestions([...clientMatches, ...leadMatches]);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchClient, isClientLocked, clients, leads]);

  const selectItem = (item) => {
    setIsClientLocked(true);
    setSelectedItem(item);
    setSearchClient("");
    setClientSuggestions([]);

    if (item.type === "client") {
      setSelectedClientId(item.id);
      setSelectedLeadId("");
      setFormData((prev) => ({
        ...prev,
        clientId: item.id,
        formLeadId: "",
        nomClient: item.nom,
        nomCompletLead: "",
      }));
    } else {
      setSelectedLeadId(item.id);
      setSelectedClientId("");
      setFormData((prev) => ({
        ...prev,
        formLeadId: item.id,
        clientId: "",
        nomCompletLead: item.displayName,
        nomClient: item.displayName,
      }));
    }
  };

  const resetClientSelection = () => {
    setIsClientLocked(false);
    setSelectedItem(null);
    setSearchClient("");
  };

  console.log("Lead sélectionné nom :", formData.nomCompletLead);

  console.log("Client sélectionné nom :", formData.nomClient);

  // Auparavant un GET /api/clients/:id à chaque sélection — ce qui faisait
  // patienter l'affichage des collaborateurs (et donc leur retrait/ajout)
  // le temps d'un aller-retour réseau. `clients` (useClients(), préchargé
  // une fois au login, voir useClients.js) contient déjà tous les champs
  // scalaires du client, y compris CLIENT_COLLAB_FIELDS : une recherche en
  // mémoire suffit, c'est instantané, pas besoin d'indicateur de chargement.
  useEffect(() => {
    if (!selectedClientId) {
      setSelectedClientEmail("");
      setClientEmailInput("");
      setSelectedClientData(null);
      return;
    }

    const data = clients.find((c) => String(c.id) === String(selectedClientId));

    setSelectedClientEmail(data?.email || "");
    setClientEmailInput("");
    setSelectedClientData(data || null);

    if (data && data.nom && data.formeSociale && data.formeSociale.length > 0) {
      setFormData((prev) => ({
        ...prev,
        nom_client: data.nom,
        forme_sociale: data.formeSociale[0],
      }));
    }
  }, [selectedClientId, clients]);

  // Pré-remplit téléphone et email depuis la fiche du client sélectionné —
  // l'email doit être celui du dossier client lui-même (affiché tel quel
  // dans le mail de notification d'événement, voir sendEmailEventController.js),
  // pas celui du collaborateur en charge du dossier. Une fois à la
  // sélection du client, toujours modifiable ensuite (pas de verrouillage,
  // simple valeur par défaut dans le state).
  useEffect(() => {
    if (!selectedClientData) return;
    setFormData((prev) => ({
      ...prev,
      tel: selectedClientData.telephone || prev.tel,
      email: selectedClientData.email || prev.email,
    }));
  }, [selectedClientData]);

  // Collaborateur par défaut depuis la fiche client — "Personne chez Alfa"
  // pour une Demande de rdv (repli id_expertComptable → id_superviseur →
  // id_collaborateur → id_collaborateur_niv2 → id_social → id_juridique),
  // ou la liste "Collaborateur(s) lié(s) à la tâche" pour les autres
  // catégories (tous les collaborateurs existants sur la fiche, case
  // "Ajouter à la liste des tâches" cochée automatiquement pour les rendre
  // visibles) — réagit aussi bien au choix du client qu'à un changement de
  // catégorie une fois le client déjà sélectionné.
  useEffect(() => {
    if (!selectedClientData) return;

    if (formData.type === "Demande de rdv") {
      const defaultCollabId = CLIENT_COLLAB_FIELDS
        .map((field) => selectedClientData[field])
        .find((id) => id);
      if (defaultCollabId) {
        setFormData((prev) => ({ ...prev, personneAlfaId: String(defaultCollabId) }));
      }
      return;
    }

    if (CATEGORIES_ASSIGNATION_AUTO.includes(formData.type)) {
      const existingCollabIds = Array.from(
        new Set(CLIENT_COLLAB_FIELDS.map((field) => selectedClientData[field]).filter(Boolean)),
      );
      if (existingCollabIds.length > 0) {
        // On garde les collaborateurs déjà associés au dossier (userIds),
        // mais "Ajouter à la liste des tâches" reste décoché par défaut —
        // c'est "Notifier à une autre personne" qui est coché par défaut
        // (voir useState notifyChecked).
        setFormData((prev) => ({ ...prev, userIds: existingCollabIds }));
      }
    }
  }, [selectedClientData, formData.type]);

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState("");
  const [progress, setProgress] = useState(0);

  // Chargement des utilisateurs au montage
  useEffect(() => {
    httpClient
      .get("https://backend-myalfa.vercel.app/api/users")
      .then((res) => {
        setAllUsers(res.data);
        const map = {};
        res.data.forEach((user) => {
          map[user.id] = user;
        });
        setUserMap(map);
      })
      .catch((err) => {
        console.error("Erreur chargement utilisateurs", err);
      });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      type: value,
      periodeArriveePC:
        value === "Arrivée pièces comptables" ? prev.periodeArriveePC : "",
      // Le champ Description est masqué pour "Demande de rdv" : on le vide
      // pour éviter qu'une valeur saisie avant le changement de catégorie
      // reste silencieusement soumise (Evenement + mail de notification).
      descriptif: value === "Demande de rdv" ? "" : prev.descriptif,
      // "Période" (mois, catégorie "Social" uniquement) : sans ce reset, une
      // valeur choisie avant de changer de catégorie resterait accrochée à
      // formData et repartirait quand même dans la requête /api/evenements.
      typeDate: value === "Social" ? prev.typeDate : "",
    }));
    // notifyUserIds est partagé entre la carte "Créer une tâche, notifier"
    // et la carte "Notifier" (pièces comptables) : sans ce reset, des
    // destinataires choisis pour une catégorie resteraient sélectionnés en
    // changeant de catégorie et pourraient être notifiés à tort (cf. audit
    // de régression). notifyChecked reste coché (valeur par défaut) — seule
    // la liste de destinataires est vidée.
    setNotifyChecked(true);
    setNotifyUserIds([]);
  };

  // Remplit les emplacements de fichiers vides (dans l'ordre bilan / identité /
  // kbis / statuts / pièce d'identité) avec les fichiers glissés-déposés ou
  // sélectionnés — au-delà de 5, les fichiers surnuméraires sont ignorés (le
  // backend n'a que ces 5 emplacements nommés).
  const handleFilesAdded = (fileList) => {
    const incoming = Array.from(fileList);
    if (incoming.length === 0) return;
    setFiles((prev) => {
      const next = { ...prev };
      let i = 0;
      for (const slot of DOCUMENT_SLOTS) {
        if (!next[slot] && incoming[i]) {
          next[slot] = incoming[i];
          i++;
        }
      }
      return next;
    });
  };

  const handleRemoveFile = (slot) => {
    setFiles((prev) => ({ ...prev, [slot]: null }));
  };

  // Suppression d'un user dans userIds
  const handleRemoveUser = (idToRemove) => {
    setFormData((prev) => ({
      ...prev,
      userIds: prev.userIds.filter((id) => id !== idToRemove),
    }));
  };

  useEffect(() => {
    const emails = formData.userIds
      .map((id) => userMap[id]?.email)
      .filter(Boolean);

    setFormData((prev) => ({
      ...prev,
      userEmails: emails,
    }));
  }, [formData.userIds, userMap]);

  const data = new FormData();

  // 3. Ajouter userEmails dans le FormData envoyé
  Object.entries(formData).forEach(([key, value]) => {
    if (key === "userIds" || key === "userEmails") {
      value.forEach((item) => data.append(`${key}[]`, item));
    } else {
      data.append(key, value);
    }
  });

  console.log("email liste : ", formData.userEmails);

  console.log(" --------- Client id liste : ", formData.clientId);
  console.log(" --------- Lead id liste : ", formData.formLeadId);
  console.log(" --------- Nom de dossier : ", formData.nomClient);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setProgress(0);
    setCurrentStep("Préparation des données...");

    const data = new FormData();

    // Ajouter champs textuels
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "userIds") {
        // Backend (createEvenement) lit req.body.userIds — pas "userIds[]"
        // (aucune conversion bracket→tableau n'est faite côté serveur, donc
        // "userIds[]" atterrissait sous une clé jamais lue et userIds
        // valait toujours [] par défaut : les collaborateurs sélectionnés
        // n'étaient jamais assignés à l'événement créé).
        value.forEach((id) => data.append("userIds", id));
      } else {
        data.append(key, value);
      }
    });

    // createEvenement (backend) attend "periodeConcernee", pas
    // "periodeArriveePC" (nom du state local) — sans ce renommage la période
    // saisie n'est jamais enregistrée sur l'Evenement, et reste donc
    // invisible dans Suivi Prod > Traitements comptables.
    data.append("periodeConcernee", formData.periodeArriveePC);

    // "Notifier une ou plusieurs personnes" — état séparé de formData.userIds
    // (sémantique différente : qui prévenir, pas qui est assigné à la tâche).
    if (formData.type === "Arrivée pièces comptables") {
      notifyUserIds.forEach((id) => data.append("notifyUserIds[]", id));
    }

    try {
      // ================================
      // 0️⃣ Upload des documents — upload direct navigateur → Blob
      // (contourne la limite 4,5 Mo), une seule fois même si le même
      // fichier est aussi rattaché au todo créé à l'étape 2.
      // ================================
      const fileEntries = Object.entries(files).filter(([, file]) => file);
      const fileUrls = {};
      if (fileEntries.length > 0) {
        setCurrentStep("Envoi du document...");
        for (const [key, file] of fileEntries) {
          const url = await uploadFileToBlob(
            file,
            "https://backend-myalfa.vercel.app/api/evenements/upload-token",
            ({ percentage }) => setProgress(Math.round(percentage * 0.2))
          );
          fileUrls[key] = url;
          data.append(`${key}Url`, url);
        }
      }

      // ================================
      // 1️⃣ Création de l'événement
      // ================================
      setCurrentStep("Création de l'événement...");
      setProgress(20);

      const response = await authFetch(
        "https://backend-myalfa.vercel.app/api/evenements",
        {
          method: "POST",
          body: data,
        },
      );

      if (!response.ok) {
        throw new Error("Erreur lors de la création de l'événement");
      }

      const result = await response.json();

      // ================================
      // 2️⃣ Création du todo (si toggle activé)
      // ================================
      if (addToTodo && formData.type !== "Arrivée pièces comptables") {
        setCurrentStep("Ajout dans la liste des tâches...");
        setProgress(40);

        try {
          const todoFormData = new FormData();

          // Champs texte
          todoFormData.append("title", formData.note);
          todoFormData.append("status", "En cours");
          todoFormData.append("type", "evenement");
          todoFormData.append("nomClient", formData.nomClient || "");
          todoFormData.append("clientId", formData.clientId || "");
          todoFormData.append("formLeadId", formData.formLeadId || "");
          todoFormData.append("eventId", result.evenement.id);
          todoFormData.append("nature", "evenement");
          todoFormData.append("dueDate", formData.dateEcheance);
          todoFormData.append("priority", "classique");
          todoFormData.append("commentaire", formData.descriptif);

          // userIds en tableau
          formData.userIds.forEach((id) => todoFormData.append("userIds", id));

          // Fichiers (si présents) — réutilise les URL déjà uploadées à
          // l'étape 0, pas de second upload du même fichier.
          Object.entries(fileUrls).forEach(([key, url]) => {
            todoFormData.append(`${key}Url`, url);
          });

          await authFetch("https://backend-myalfa.vercel.app/api/todos", {
            method: "POST",
            body: todoFormData, // ⚠️ PAS de headers pour multipart/form-data
          });

          console.log("Todo créé avec succès !");
        } catch (todoError) {
          console.error("Erreur lors de la création du todo :", todoError);
        }
      }
      // ================================
      // 3️⃣ Envoi mail (confirmation RDV client, ou notification événement générique)
      // ================================
      setCurrentStep("Envoi des notifications...");
      setProgress(60);
      if (formData.type === "Demande de rdv") {
        // Si le client n'avait pas d'email, on l'enregistre sur sa fiche
        // avant d'envoyer la confirmation, pour que le backend le retrouve.
        if (
          !selectedClientEmail &&
          clientEmailInput.trim() &&
          formData.clientId
        ) {
          try {
            await authFetch(
              `https://backend-myalfa.vercel.app/api/clients/${formData.clientId}`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: clientEmailInput.trim() }),
              },
            );
            console.log("Email du client enregistré sur sa fiche");
          } catch (clientEmailError) {
            console.error(
              "Erreur lors de l'enregistrement de l'email du client :",
              clientEmailError,
            );
          }
        }

        try {
          await authFetch(
            "https://backend-myalfa.vercel.app/api/send-rdv-email-client",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                clientId: formData.clientId,
                personneAlfaId: formData.personneAlfaId,
                dateRdv: formData.dateRdv,
                heureRdv: formData.heureRdv,
                siteRdv: formData.siteRdv,
              }),
            },
          );
          console.log("Email de confirmation RDV envoyé");
        } catch (mailError) {
          console.error(
            "Erreur lors de l'envoi de l'email de confirmation RDV :",
            mailError,
          );
        }

        // "Notifier à une autre personne" : send-rdv-email-client ne prend
        // pas de destinataires libres (il notifie uniquement le client et la
        // personne Alfa du rdv), donc on envoie en plus une notification
        // générique aux personnes choisies (sinon la case n'a aucun effet —
        // cf. audit de régression).
        if (notifyChecked && notifyUserIds.length > 0) {
          try {
            const notifyEmails = notifyUserIds
              .map((id) => userMap[id]?.email)
              .filter(Boolean);
            if (notifyEmails.length > 0) {
              await authFetch(
                "https://backend-myalfa.vercel.app/api/mail-event",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    note: formData.note,
                    descriptif: formData.descriptif,
                    categorie: formData.categorie,
                    type: formData.type,
                    nomCompletLead: formData.nomCompletLead,
                    nomClient: formData.nomClient,
                    userEmails: notifyEmails,
                    // Sans ces 4 champs, la personne notifiée ici ne voit ni
                    // avec qui, ni quand, ni où a lieu le rendez-vous —
                    // contrairement à la personne assignée directement
                    // (send-rdv-email-client) — signalé par un client.
                    dateRdv: formData.dateRdv,
                    heureRdv: formData.heureRdv,
                    siteRdv: formData.siteRdv,
                    personneAlfaNom: (() => {
                      const u = userMap[formData.personneAlfaId];
                      return u ? `${u.name} ${u.lastName}`.trim() : "";
                    })(),
                  }),
                },
              );
              console.log("Email de notification (Demande de rdv) envoyé");
            }
          } catch (mailError) {
            console.error(
              "Erreur lors de l'envoi de l'email de notification (Demande de rdv) :",
              mailError,
            );
          }
        }
      } else if (formData.type === "Arrivée pièces comptables") {
        // Mise à jour optimiste INSTANTANÉE des deux tableaux, à partir de la
        // réponse du POST /api/evenements elle-même (qui contient déjà le
        // ticket créé + ses étapes pré-affectées — voir createEvenement côté
        // backend) : pas besoin d'attendre un refetch réseau pour que
        // l'utilisateur voie le dossier apparaître.
        const optimisticTicket = buildOptimisticTicket(
          result.evenement,
          allUsers,
          formData.nomClient,
        );
        if (optimisticTicket) {
          setTraitementsComptablesData((prev) => [optimisticTicket, ...prev]);
        }
        const maintenant = new Date();
        patchSuiviComptaTacheStatut(
          result.evenement.clientId,
          maintenant.getFullYear(),
          maintenant.getMonth() + 1,
          "Arrive PC",
        );

        // La création est déjà validée côté backend à ce stade : l'overlay se
        // ferme tout de suite, le reste (notification, historique,
        // resynchronisation complète) continue en arrière-plan et est suivi
        // par le toast ci-dessous plutôt que par l'overlay.
        onCancel();

        const ticketToast = showStatusToast({
          status: "loading",
          titre: "Ajout du dossier…",
          message: "Intégration de la demande dans l'ensemble des systèmes…",
        });

        // On notifie, en plus, les personnes choisies dans "Notifier une ou
        // plusieurs personnes".
        if (notifyChecked && notifyUserIds.length > 0) {
          try {
            const notifyEmails = notifyUserIds
              .map((id) => userMap[id]?.email)
              .filter(Boolean);
            await authFetch(
              "https://backend-myalfa.vercel.app/api/email-traitement-comptable-creation",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  clientNom: formData.nomClient,
                  nature: formData.descriptif,
                  periodeConcernee: formData.periodeArriveePC,
                  userEmails: notifyEmails,
                }),
              },
            );
            console.log(
              "Email de notification (arrivée pièces comptables) envoyé",
            );
          } catch (mailError) {
            console.error(
              "Erreur lors de l'envoi de l'email de notification :",
              mailError,
            );
          }
        }

        await Promise.all([fetchTraitementsComptables(), fetchSuiviCompta()]);
        ticketToast.update({
          status: "success",
          titre: "Dossier ajouté",
          message: `${
            formData.nomClient || "Le dossier"
          } apparaît maintenant dans Traitements comptables (mission "Arrive PC" validée dans Suivi Compta).`,
        });
      } else {
        try {
          // "Notifier à une autre personne" : destinataires supplémentaires,
          // fusionnés avec les collaborateurs déjà notifiés via userEmails
          // (sinon la case n'a aucun effet — cf. audit de régression).
          const notifyEmails =
            notifyChecked && notifyUserIds.length > 0
              ? notifyUserIds.map((id) => userMap[id]?.email).filter(Boolean)
              : [];
          const mailPayload = {
            note: formData.note,
            descriptif: formData.descriptif,
            categorie: formData.categorie,
            type: formData.type,
            typeDate: formData.typeDate,
            tel: formData.tel,
            email: formData.email,
            sms: formData.sms,
            acceuil: formData.acceuil,
            dateEcheance: formData.dateEcheance,
            userEmails: [...formData.userEmails, ...notifyEmails],
            nomCompletLead: formData.nomCompletLead,
            nomClient: formData.nomClient,
            bilanDocument: result.evenement.bilanDocument,
            identityDocument: result.evenement.identityDocument,
            kbisDocument: result.evenement.kbisDocument,
            statusDocument: result.evenement.statusDocument,
            pieceIdentite: result.evenement.pieceIdentite,
          };

          await authFetch("https://backend-myalfa.vercel.app/api/mail-event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(mailPayload),
          });

          console.log("Email envoyé avec succès via mail-event");
        } catch (mailError) {
          console.error(
            "Erreur lors de l'envoi du mail via mail-event :",
            mailError,
          );
        }
      }

      // ================================
      // 4️⃣ Création historique client / lead
      // ================================
      if (formData.clientId) {
        setCurrentStep("Création de l'historique client...");
        setProgress(80);

        const isRdv = formData.type === "Demande de rdv";
        const dateRdvFormatted = formData.dateRdv
          ? new Date(formData.dateRdv).toLocaleDateString("fr-FR")
          : "";
        // La description est obligatoire côté backend : pour une "Demande de
        // rdv" et pour "Arrivée pièces comptables" on ne dépend pas du champ
        // "Titre" (masqué pour ces catégories) et on construit un résumé à
        // partir de leurs propres champs.
        const description = isRdv
          ? `Rendez-vous le ${dateRdvFormatted} à ${formData.heureRdv} — ${formData.siteRdv}`
          : isPieces
            ? `Arrivée pièces comptables — ${formData.periodeArriveePC}${
                formData.descriptif ? ` — ${formData.descriptif}` : ""
              }`
            : formData.note;
        const titre = isRdv
          ? "Demande de rendez-vous"
          : isPieces
            ? "Arrivée pièces comptables"
            : "création d'évènement";

        const clientIdNum = Number(formData.clientId);
        const clientQueryKey = ["client", clientIdNum];

        // Mise à jour optimiste INSTANTANÉE de la fiche client (si déjà
        // chargée dans le cache — FicheClientOverlay.js lit client.historiques
        // via useClient(id)) : sans ça, la timeline ne montre le nouvel
        // événement qu'au prochain rechargement complet de la fiche, ce qui a
        // fait croire à une perte de traçabilité (signalé par un client).
        const optimisticEntry = {
          id: `optimistic-${Date.now()}`,
          titre,
          description,
          clientId: clientIdNum,
          eventId: result.evenement.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const previousClient = queryClient.getQueryData(clientQueryKey);
        if (previousClient) {
          queryClient.setQueryData(clientQueryKey, {
            ...previousClient,
            historiques: [
              optimisticEntry,
              ...(previousClient.historiques || []),
            ],
          });
        }

        const histToast = showStatusToast({
          status: "loading",
          titre: "Historique client",
          message:
            "Déjà visible dans la fiche du client — enregistrement en arrière-plan…",
        });

        try {
          await authFetch(
            "https://backend-myalfa.vercel.app/api/historique-client",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                titre,
                description,
                clientId: formData.clientId,
                eventId: result.evenement.id,
              }),
            },
          );
          console.log("Historique client créé avec succès !");
          histToast.update({
            status: "success",
            titre: "Historique client",
            message: "Événement bien enregistré dans la timeline du client.",
          });
        } catch (histError) {
          console.error(
            "Erreur lors de la création de l'historique client :",
            histError,
          );
          // L'enregistrement réel a échoué : on retire l'entrée optimiste
          // plutôt que de laisser une timeline mentir sur ce qui est
          // vraiment persisté.
          if (previousClient) {
            queryClient.setQueryData(clientQueryKey, previousClient);
          }
          histToast.update({
            status: "error",
            titre: "Historique client",
            message:
              "L'événement n'a pas pu être ajouté à la timeline du client — réessayez depuis sa fiche si besoin.",
          });
        }
      } else if (formData.formLeadId) {
        setCurrentStep("Création de l'historique prospect...");
        setProgress(80);

        const isRdv = formData.type === "Demande de rdv";
        const dateRdvFormatted = formData.dateRdv
          ? new Date(formData.dateRdv).toLocaleDateString("fr-FR")
          : "";
        const description = isRdv
          ? `Rendez-vous le ${dateRdvFormatted} à ${formData.heureRdv} — ${formData.siteRdv}`
          : isPieces
            ? `Arrivée pièces comptables — ${formData.periodeArriveePC}${
                formData.descriptif ? ` — ${formData.descriptif}` : ""
              }`
            : formData.note;
        const titre = isRdv
          ? "Demande de rendez-vous"
          : isPieces
            ? "Arrivée pièces comptables"
            : "création d'évènement";

        const formLeadIdNum = Number(formData.formLeadId);

        // Même logique de mise à jour optimiste que pour un client (voir
        // ci-dessus) — leads est un state global (pas React Query), donc on
        // patch directement via setLeads.
        const optimisticEntry = {
          id: `optimistic-${Date.now()}`,
          titre,
          description,
          FormLEADId: formLeadIdNum,
          eventId: result.evenement.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const previousLeads = leads;
        setLeads((prev) =>
          prev.map((l) =>
            l.id === formLeadIdNum
              ? {
                  ...l,
                  HistoriqueFormLEAD: [
                    optimisticEntry,
                    ...(l.HistoriqueFormLEAD || []),
                  ],
                }
              : l,
          ),
        );

        const histToastLead = showStatusToast({
          status: "loading",
          titre: "Historique prospect",
          message:
            "Déjà visible dans la fiche du prospect — enregistrement en arrière-plan…",
        });

        try {
          await authFetch(
            "https://backend-myalfa.vercel.app/api/historique-formLead",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                titre,
                description,
                FormLEADId: formLeadIdNum,
                eventId: result.evenement.id,
              }),
            },
          );
          console.log("Historique prospect créé avec succès !");
          histToastLead.update({
            status: "success",
            titre: "Historique prospect",
            message: "Événement bien enregistré dans la timeline du prospect.",
          });
        } catch (histError) {
          console.error(
            "Erreur lors de la création de l'historique prospect :",
            histError,
          );
          setLeads(previousLeads);
          histToastLead.update({
            status: "error",
            titre: "Historique prospect",
            message:
              "L'événement n'a pas pu être ajouté à la timeline du prospect — réessayez depuis sa fiche si besoin.",
          });
        }
      }

      // ================================
      // 5️⃣ Reset form
      // ================================
      setCurrentStep("Finalisation...");
      setProgress(100);

      setFormData({
        formLeadId: "",
        clientId: "",
        note: "",
        descriptif: "",
        categorie: "",
        type: "",
        typeDate: "",
        tel: "",
        email: "",
        sms: "",
        acceuil: "",
        dateRdv: "",
        heureRdv: "",
        siteRdv: "",
        personneAlfaId: "",
        userIds: [],
        periodeArriveePC: "",
      });

      setFiles({
        bilanDocument: null,
        identityDocument: null,
        kbisDocument: null,
        statusDocument: null,
        pieceIdentite: null,
      });

      setSelectedClientEmail("");
      setClientEmailInput("");
      setAddToTodo(false);
      setNotifyChecked(true);
      setNotifyUserIds([]);
    } catch (error) {
      console.error(error);
      dialog.alert("Une erreur est survenue lors de la soumission");
    } finally {
      setLoading(false);
      setCurrentStep("");
      setProgress(0);
      onCancel();
    }
  };

  useEffect(() => {
    console.log("Lead sélectionné id (useEffect) :", selectedLeadId);
  }, [selectedLeadId]);

  useEffect(() => {
    console.log("Client sélectionné id (useEffect) :", selectedClientId);
  }, [selectedClientId]);

  const isFormInvalid =
    !formData.nomClient?.trim() ||
    (!formData.clientId && !formData.formLeadId) ||
    !formData.type ||
    (formData.type === "Arrivée pièces comptables" &&
      (!formData.periodeArriveePC?.trim() ||
        !notifyChecked ||
        notifyUserIds.length === 0)) ||
    // Notifier au moins une personne est requis pour valider la tâche, sur
    // toutes les catégories sauf "Demande de rdv" (déjà notifiée via l'email
    // de confirmation au client + à la personne Alfa du rdv).
    (formData.type &&
      formData.type !== "Arrivée pièces comptables" &&
      formData.type !== "Demande de rdv" &&
      (!notifyChecked || notifyUserIds.length === 0));

  const isPieces = formData.type === "Arrivée pièces comptables";

  return (
    // zoom: la maquette source (styles.css) applique `body { zoom: 0.85 }`
    // globalement — les tailles en px reprises ici (13px, 20px...) sont donc
    // calibrées pour ce zoom. On le reproduit ici pour obtenir le même rendu.
    <div className="flex flex-col w-full h-full pt-5" style={{ zoom: 0.85 }}>
      {/* En-tête */}
      <div className="flex items-center gap-3.5 px-6 pb-[18px] border-b border-[#ECE5E2] shrink-0">
        <div className="w-11 h-11 rounded-lg bg-[#F7E9EE] text-[#6E1A36] flex items-center justify-center flex-shrink-0">
          <CalendarDays className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="m-0 text-[20px] font-semibold text-[#1F1620] leading-tight">
            Création d'Évènement ou Tâche
          </h2>
          <p className="mt-1 text-[12.5px] text-[#8A7780]">
            Planifier une action sur un dossier
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="w-9 h-9 rounded-lg border border-[#ECE5E2] bg-white flex items-center justify-center text-[#1F1620] hover:bg-[#FAF7F4] transition-colors flex-shrink-0"
        >
          <X size={16} />
        </button>
      </div>

      <form
        className="w-full px-6 py-3.5 overflow-y-auto flex-1"
        onSubmit={handleSubmit}
        encType="multipart/form-data"
      >
        {/* Informations essentielles */}
        <div className="rounded-xl mb-3 border border-[#E8C8D2] bg-[#FFF6F8] px-4 py-3.5">
          <div className="flex items-center gap-2 mb-2.5 text-[13px] font-semibold text-[#6E1A36]">
            <AlertCircle className="w-[15px] h-[15px]" />
            Informations essentielles
          </div>

          <label className="block text-[12.5px] font-medium text-[#4D3F45] mt-2 mb-1">
            Client / Lead{" "}
            <span className="text-[#C03A2B] font-normal italic">
              (champ obligatoire à sélectionner*)
            </span>
          </label>

          {isClientLocked && selectedItem ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-[#6E1A36]/30 bg-[#F7E9EE] px-3.5 py-2.5">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-[#6E1A36] flex items-center justify-center flex-shrink-0">
                  {selectedItem.type === "client" ? (
                    <Building2 className="w-4 h-4 text-white" />
                  ) : (
                    <User className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className="min-w-0 flex items-center gap-1.5">
                  <span className="text-[13px] font-semibold text-[#1F1620] truncate">
                    {selectedItem.type === "client" ? selectedItem.nom : selectedItem.displayName}
                  </span>
                  <span
                    className="text-[9.5px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                    style={{
                      background: selectedItem.type === "client" ? "#d1fae5" : "#fef08a",
                      color: selectedItem.type === "client" ? "#065f46" : "#b91c1c",
                    }}
                  >
                    {selectedItem.type === "client" ? "Client" : "Prospect"}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={resetClientSelection}
                className="text-[12px] font-medium text-[#6E1A36] hover:underline flex-shrink-0"
              >
                Modifier
              </button>
            </div>
          ) : (
            <div className="relative w-full">
              <input
                type="text"
                value={searchClient}
                onChange={(e) => setSearchClient(e.target.value)}
                placeholder="Rechercher un client ou un prospect…"
                autoComplete="off"
                className="w-full h-10 rounded-lg bg-white px-3 text-[13px] text-[#1F1620] outline-none transition-all border border-[#ECE5E2] focus:border-[#6E1A36]"
              />

              {clientSuggestions.length > 0 && (
                <ul className="absolute left-0 top-full mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-[#ECE5E2] bg-white shadow-[0_10px_28px_rgba(0,0,0,.10)] z-20">
                  {clientSuggestions.map((item) => (
                    <li
                      key={`${item.type}-${item.id}`}
                      onClick={() => selectItem(item)}
                      className="flex items-center justify-between gap-2 cursor-pointer px-3 py-2 text-[13px] text-[#1F1620] hover:bg-[#FFF6EE] transition-colors border-b border-[#ECE5E2] last:border-b-0"
                    >
                      <span className="truncate">
                        {item.type === "client" ? item.nom : item.displayName}
                      </span>
                      <span
                        className="text-[9.5px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                        style={{
                          background: item.type === "client" ? "#d1fae5" : "#fef08a",
                          color: item.type === "client" ? "#065f46" : "#b91c1c",
                        }}
                      >
                        {item.type === "client" ? "Client" : "Prospect"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {searchClient.trim().length >= 2 && clientSuggestions.length === 0 && (
                <p className="text-[11.5px] text-[#8A7780] italic mt-1">Aucun résultat trouvé.</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <label className="block text-[12.5px] font-medium text-[#4D3F45] mb-1">
                Canal
              </label>
              <div className="relative rounded-lg border border-[#ECE5E2] bg-white flex items-center">
                <select
                  name="categorie"
                  value={formData.categorie}
                  onChange={handleInputChange}
                  className="flex-1 appearance-none bg-transparent border-none py-2.5 pl-3 pr-9 text-[13px] text-[#1F1620] outline-none cursor-pointer"
                >
                  <option value="">— Choisir le canal —</option>
                  <option value="telephone">Téléphone</option>
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="accueil">Accueil</option>
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-3 pointer-events-none text-[#8A7780]"
                />
              </div>
            </div>
            <div>
              <label className="block text-[12.5px] font-medium text-[#4D3F45] mb-1">
                Catégorie <span className="text-[#C03A2B]">*</span>
              </label>
              <div className="relative rounded-lg border border-[#ECE5E2] bg-white flex items-center">
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleTypeChange}
                  className="flex-1 appearance-none bg-transparent border-none py-2.5 pl-3 pr-9 text-[13px] text-[#1F1620] outline-none cursor-pointer"
                >
                  <option value="">— Choisir la catégorie —</option>
                  <option value="Arrivée pièces comptables">
                    Arrivée pièces comptables
                  </option>
                  <option value="Compta / fiscal">Compta / fiscal</option>
                  <option value="Social">Social</option>
                  <option value="Juridique">Juridique</option>
                  <option value="Demande de rdv">Demande de rdv</option>
                  <option value="Autre">Autre</option>
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-3 pointer-events-none text-[#8A7780]"
                />
              </div>
            </div>
          </div>

          {isPieces && (
            <div className="flex gap-2 items-start rounded-lg mt-2 px-3.5 py-3 bg-[#F7E9EE] border border-[#6E1A36]/20 text-[#6E1A36] text-[12.5px] leading-[1.45]">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              <span>
                L'arrivée des pièces alimente l'écran{" "}
                <strong>Suivi Prod › Traitements comptables</strong>.
                L'affectation d'un collaborateur par niveau d'intervention s'y
                fait ensuite.
              </span>
            </div>
          )}
        </div>

        {/* Détail / Détail de l'évènement */}
        {formData.type && (
          <div className="rounded-xl mb-3 border border-[#ECE5E2] bg-[#FAFAF8] px-4 py-3.5">
            <div className="flex items-center gap-2 mb-2.5 text-[13px] font-semibold text-[#6E1A36]">
              <FileText className="w-[15px] h-[15px]" />
              Détail{!isPieces && " de l'évènement"}
            </div>

            {!isPieces && (
              <>
                <label className="block text-[12.5px] font-medium text-[#4D3F45] mt-2 mb-1">
                  Titre
                </label>
                <input
                  className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all"
                  name="note"
                  type="text"
                  value={formData.note}
                  onChange={handleInputChange}
                  placeholder="Raison sociale…"
                />

                {formData.categorie === "telephone" && (
                  <>
                    <label className="block text-[12.5px] font-medium text-[#4D3F45] mt-2 mb-1">
                      Téléphone
                    </label>
                    <input
                      className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all"
                      type="tel"
                      name="tel"
                      placeholder="Téléphone"
                      value={formData.tel}
                      onChange={handleInputChange}
                    />
                  </>
                )}
                {formData.categorie === "email" && (
                  <>
                    <label className="block text-[12.5px] font-medium text-[#4D3F45] mt-2 mb-1">
                      Email
                    </label>
                    <input
                      className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all"
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </>
                )}
                {formData.categorie === "sms" && (
                  <>
                    <label className="block text-[12.5px] font-medium text-[#4D3F45] mt-2 mb-1">
                      SMS
                    </label>
                    <input
                      className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all"
                      type="text"
                      name="sms"
                      placeholder="SMS"
                      value={formData.sms}
                      onChange={handleInputChange}
                    />
                  </>
                )}
                {formData.categorie === "accueil" && (
                  <>
                    <label className="block text-[12.5px] font-medium text-[#4D3F45] mt-2 mb-1">
                      Accueil
                    </label>
                    <input
                      className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all"
                      type="text"
                      name="acceuil"
                      placeholder="Accueil"
                      value={formData.acceuil}
                      onChange={handleInputChange}
                    />
                  </>
                )}
              </>
            )}

            {isPieces && (
              <>
                <label className="block text-[12.5px] font-medium text-[#4D3F45] mt-2 mb-1">
                  Période <span className="text-[#C03A2B]">*</span>
                </label>
                <input
                  type="text"
                  value={formData.periodeArriveePC}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      periodeArriveePC: e.target.value,
                    }))
                  }
                  placeholder="ex. Juin 2026, 2e trimestre, Exercice 2025…"
                  className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all"
                />
              </>
            )}

            {formData.type === "Social" && (
              <>
                <label className="block text-[12.5px] font-medium text-[#4D3F45] mt-2 mb-1">
                  Période
                </label>
                {/* Deux selects Mois + Annee : input type="month" n'est pas supporte
                    par Safari (retombe en texte libre illisible). typeDate reste
                    stocke en "yyyy-MM-01" pour le champ DateTime en base. */}
                {(() => {
                  const MOIS = [
                    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
                  ];
                  const anneeCourante = new Date().getFullYear();
                  const ANNEES = Array.from({ length: 6 }, (_, i) => anneeCourante - 3 + i);
                  const td = formData.typeDate || "";
                  const selMois = td ? String(parseInt(td.slice(5, 7), 10)) : "";
                  const selAnnee = td ? td.slice(0, 4) : "";
                  const setPeriode = (mois, annee) => {
                    setFormData((prev) => ({
                      ...prev,
                      typeDate: mois && annee
                        ? `${annee}-${String(mois).padStart(2, "0")}-01`
                        : "",
                    }));
                  };
                  const selectCls =
                    "h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all cursor-pointer";
                  return (
                    <div className="grid grid-cols-2 gap-3">
                      <select
                        className={selectCls}
                        value={selMois}
                        onChange={(e) =>
                          setPeriode(e.target.value, selAnnee || String(anneeCourante))
                        }
                      >
                        <option value="">— Mois —</option>
                        {MOIS.map((m, i) => (
                          <option key={m} value={i + 1}>{m}</option>
                        ))}
                      </select>
                      <select
                        className={selectCls}
                        value={selAnnee}
                        onChange={(e) =>
                          setPeriode(
                            selMois || String(new Date().getMonth() + 1),
                            e.target.value
                          )
                        }
                      >
                        <option value="">— Année —</option>
                        {ANNEES.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}
              </>
            )}

            {formData.type === "Demande de rdv" && (
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12.5px] font-medium text-[#4D3F45] mb-1">
                      Date de rendez-vous
                    </label>
                    <input
                      className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all"
                      type="date"
                      name="dateRdv"
                      value={formData.dateRdv || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-[12.5px] font-medium text-[#4D3F45] mb-1">
                      Heure de rendez-vous
                    </label>
                    <input
                      className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all"
                      type="time"
                      name="heureRdv"
                      value={formData.heureRdv || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12.5px] font-medium text-[#4D3F45] mb-1">
                      Site
                    </label>
                    <select
                      name="siteRdv"
                      value={formData.siteRdv || ""}
                      onChange={handleInputChange}
                      className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all"
                    >
                      <option value="">— Site —</option>
                      <option value="27 rue Vallon Hoarau, LE TAMPON (97430)">
                        27 rue Vallon Hoarau, LE TAMPON (97430)
                      </option>
                      <option value="19 chemin du Château d'Eau, Espace Turquoise n° 23, SAINT-PIERRE (97410)">
                        19 ch. Château d'Eau, Espace Turquoise, ST-PIERRE
                        (97410)
                      </option>
                      <option value="RDV en ligne">RDV en ligne</option>
                      <option value="Rendez-vous téléphonique">Rendez-vous téléphonique</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12.5px] font-medium text-[#4D3F45] mb-1">
                      Personne chez Alfa
                    </label>
                    <select
                      name="personneAlfaId"
                      value={formData.personneAlfaId || ""}
                      onChange={handleInputChange}
                      className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all"
                    >
                      <option value="">— Collaborateur —</option>
                      {allUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name} {user.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {selectedClientId && !selectedClientEmail && (
                  <div className="rounded-lg bg-[#FFF8DC] border border-[#8B6A0F]/25 p-3 space-y-2">
                    <p className="text-xs text-[#8B6A0F] font-medium">
                      Ce client n'a pas d'adresse email enregistrée.
                      Saisissez-en une pour envoyer la confirmation de
                      rendez-vous — elle sera aussi enregistrée sur la fiche du
                      client.
                    </p>
                    <input
                      type="email"
                      value={clientEmailInput}
                      onChange={(e) => setClientEmailInput(e.target.value)}
                      placeholder="email@exemple.com"
                      className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#8B6A0F]/40 outline-none focus:border-[#6E1A36] transition-all"
                    />
                  </div>
                )}
              </div>
            )}

            {formData.type !== "Demande de rdv" && (
              <>
                <label className="block text-[12.5px] font-medium text-[#4D3F45] mt-2 mb-1">
                  {isPieces ? "Natures des pièces" : "Description"}
                </label>
                <textarea
                  name="descriptif"
                  placeholder="Descriptif"
                  value={formData.descriptif}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full rounded-lg border border-[#ECE5E2] px-3 py-2.5 text-[13px] text-[#1F1620] bg-white outline-none focus:border-[#6E1A36] transition-all resize-y"
                />
              </>
            )}
          </div>
        )}

        {/* Créer une tâche, notifier (hors arrivée pièces comptables) */}
        {formData.type && !isPieces && (
          <div className="rounded-xl mb-3 border border-[#ECE5E2] bg-[#FAFAF8] px-4 py-3.5">
            <div className="flex items-center gap-2 mb-2.5 text-[13px] font-semibold text-[#6E1A36]">
              <CalendarDays className="w-[15px] h-[15px]" />
              Créer une tâche, notifier
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none text-[13px] text-[#4D3F45]">
              <input
                type="checkbox"
                checked={addToTodo}
                onChange={(e) => setAddToTodo(e.target.checked)}
                className="accent-[#6E1A36] w-3.5 h-3.5"
              />
              Ajouter à la liste des tâches
            </label>

            {/* Visible pour les catégories où des collaborateurs sont
                auto-assignés depuis la fiche client (voir l'effet plus haut),
                même si la case "Ajouter à la liste des tâches" n'est pas
                cochée — ces collaborateurs sont notifiés par mail qu'une
                tâche soit créée ou non (voir userEmails à la soumission).
                Basé sur la catégorie et non sur formData.userIds.length : si
                on gardait la section visible seulement tant qu'il reste des
                collaborateurs, la vider entièrement (tout retirer) faisait
                disparaître aussi le champ de recherche, empêchant d'en
                rajouter un autre. */}
            {(addToTodo || CATEGORIES_ASSIGNATION_AUTO.includes(formData.type)) && (
              <div className="my-2 px-3 py-2.5 rounded-[10px] bg-[#FFF6EE] border border-[#ECE5E2]">
                <label className="block text-[12.5px] font-medium text-[#4D3F45] mb-1">
                  Collaborateur(s) lié(s) à la tâche
                </label>
                <div className="relative w-full">
                  <input
                    type="text"
                    value={searchUser}
                    onChange={handleSearchUser}
                    placeholder="Rechercher un utilisateur…"
                    autoComplete="off"
                    className="w-full h-10 rounded-lg bg-white px-3 text-[13px] text-[#1F1620] outline-none transition-all border border-[#ECE5E2] focus:border-[#6E1A36]"
                  />

                  {userSuggestions.length > 0 && (
                    <ul className="absolute left-0 top-full mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-[#ECE5E2] bg-white shadow-[0_10px_28px_rgba(0,0,0,.10)] z-20">
                      {userSuggestions.map((user) => (
                        <li
                          key={user.id}
                          onClick={() => selectUser(user)}
                          className="cursor-pointer px-3 py-2 text-[13px] text-[#1F1620] hover:bg-[#FFF6EE] transition-colors border-b border-[#ECE5E2] last:border-b-0"
                        >
                          {user.name} {user.lastName}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {formData.userIds.length === 0 ? (
                  <p className="text-[12.5px] text-[#8A7780] italic mt-1.5">
                    Aucun utilisateur ajouté.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {formData.userIds.map((id) => (
                      <div
                        key={id}
                        className="flex items-center gap-1.5 bg-white border border-[#ECE5E2] text-[#1F1620] pl-1 pr-1.5 py-1 rounded-full text-xs"
                      >
                        <span className="inline-flex items-center justify-center rounded-full bg-[#6E1A36] text-white w-[18px] h-[18px] text-[9px] font-semibold flex-shrink-0">
                          {userMap[id]?.name?.[0]}
                          {userMap[id]?.lastName?.[0]}
                        </span>
                        {userMap[id]?.name || "Utilisateur inconnu"}{" "}
                        {userMap[id]?.lastName || ""}
                        <button
                          type="button"
                          onClick={() => handleRemoveUser(id)}
                          className="text-[#8A7780] hover:text-[#6E1A36] rounded-full p-0.5 transition-colors"
                        >
                          <X size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <label className="block text-[12.5px] font-medium text-[#4D3F45] mt-2 mb-1">
              Date d'échéance
            </label>
            <input
              className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#1F1620] border border-[#ECE5E2] outline-none focus:border-[#6E1A36] transition-all"
              type="date"
              name="dateEcheance"
              value={formData.dateEcheance || ""}
              onChange={handleInputChange}
            />

            <label className="flex items-center gap-2 cursor-pointer select-none text-[13px] text-[#4D3F45] mt-3">
              <input
                type="checkbox"
                checked={notifyChecked}
                onChange={(e) => setNotifyChecked(e.target.checked)}
                className="accent-[#6E1A36] w-3.5 h-3.5"
              />
              Notifier à une autre personne
              {formData.type !== "Demande de rdv" && (
                <span className="text-[#C03A2B] font-normal italic">
                  (notifier une personne au minimum*)
                </span>
              )}
            </label>

            {notifyChecked && (
              <div className="mt-2 px-3 py-2.5 rounded-[10px] bg-[#FFF6EE] border border-[#ECE5E2]">
                <UserSearchTagPicker
                  allUsers={allUsers}
                  label="Personne(s) à notifier"
                  selectedIds={notifyUserIds}
                  onAdd={(user) => setNotifyUserIds((ids) => [...ids, user.id])}
                  onRemove={(id) =>
                    setNotifyUserIds((ids) => ids.filter((i) => i !== id))
                  }
                  emptyText="Aucune personne ajoutée."
                />
              </div>
            )}
          </div>
        )}

        {/* Notifier (arrivée pièces comptables) */}
        {isPieces && (
          <div className="rounded-xl mb-3 border border-[#ECE5E2] bg-[#FAFAF8] px-4 py-3.5">
            <div className="flex items-center gap-2 mb-2.5 text-[13px] font-semibold text-[#6E1A36]">
              <Bell className="w-[15px] h-[15px]" />
              Notifier
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none text-[13px] text-[#4D3F45]">
              <input
                type="checkbox"
                checked={notifyChecked}
                onChange={(e) => setNotifyChecked(e.target.checked)}
                className="accent-[#6E1A36] w-3.5 h-3.5"
              />
              Notifier une ou plusieurs personnes{" "}
              <span className="text-[#C03A2B] font-normal italic">
                (notifier une personne au minimum*)
              </span>
            </label>
            {notifyChecked && (
              <div className="mt-2">
                <UserSearchTagPicker
                  allUsers={allUsers}
                  selectedIds={notifyUserIds}
                  onAdd={(user) => setNotifyUserIds((ids) => [...ids, user.id])}
                  onRemove={(id) =>
                    setNotifyUserIds((ids) => ids.filter((i) => i !== id))
                  }
                />
              </div>
            )}
          </div>
        )}

        {/* Documents */}
        <div className="rounded-xl border border-[#ECE5E2] bg-[#FAFAF8] px-4 py-3">
          <button
            type="button"
            onClick={() => setChaqueAssocieDocument(!ChaqueAssocieDocument)}
            className="w-full flex items-center gap-2 bg-transparent border-none cursor-pointer text-[13px] font-semibold text-[#1F1620] p-0"
          >
            <FileText className="w-[15px] h-[15px]" />
            Envoyer des documents avec cette tâche (optionnel)
            <ChevronRight
              size={14}
              className={`ml-auto text-[#8A7780] transition-transform ${
                ChaqueAssocieDocument ? "rotate-90" : ""
              }`}
            />
          </button>
          {ChaqueAssocieDocument && (
            <div className="mt-2.5">
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDraggingFiles(true);
                }}
                onDragLeave={() => setIsDraggingFiles(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingFiles(false);
                  handleFilesAdded(e.dataTransfer.files);
                }}
                className={`flex items-center justify-center gap-2 border-[1.5px] border-dashed rounded-lg py-[22px] cursor-pointer transition-colors ${
                  isDraggingFiles
                    ? "border-[#6E1A36] bg-[#6E1A36]/5 text-[#6E1A36]"
                    : "border-[#DDD2CE] bg-white text-[#8A7780] hover:border-[#6E1A36] hover:text-[#6E1A36]"
                }`}
              >
                <Plus size={16} />
                <span className="text-[13px]">
                  Glisser ou cliquer pour ajouter des fichiers
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept=".docx,.pdf,.png,.jpg,.jpeg,.webp,.heic,.xls,.xlsx"
                onChange={(e) => {
                  handleFilesAdded(e.target.files);
                  e.target.value = "";
                }}
              />

              {DOCUMENT_SLOTS.some((slot) => files[slot]) && (
                <ul className="list-none p-0 mt-2 flex flex-col gap-1">
                  {DOCUMENT_SLOTS.filter((slot) => files[slot]).map((slot) => (
                    <li
                      key={slot}
                      className="flex items-center gap-2 text-[12.5px] text-[#4D3F45]"
                    >
                      <FileText size={13} className="flex-shrink-0" />
                      <span className="truncate">{files[slot].name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(slot)}
                        className="ml-auto text-[#8A7780] hover:text-[#6E1A36] flex-shrink-0"
                      >
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </form>

      {/* Pied de page fixe */}
      <div className="shrink-0 flex items-center justify-between gap-2 px-[18px] py-3.5 border-t border-[#ECE5E2] bg-[#FAFAF8]">
        <button
          type="button"
          onClick={onCancel}
          className="px-[18px] h-10 rounded-lg border border-[#ECE5E2] bg-white text-[#1F1620] text-[13.5px] font-medium hover:bg-[#FAF7F4] transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={loading || isFormInvalid}
          onClick={handleSubmit}
          className={`flex items-center gap-2 px-[22px] h-10 rounded-lg text-white text-[13.5px] font-medium transition-colors ${
            loading || isFormInvalid
              ? "bg-[#6E1A36]/50 cursor-not-allowed"
              : "bg-[#6E1A36] hover:bg-[#8C2A4A]"
          }`}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {currentStep}
            </>
          ) : (
            <>
              <Check className="w-[14px] h-[14px]" />
              {isPieces ? "Enregistrer l'arrivée" : "Créer la tâche"}
            </>
          )}
        </button>
      </div>
      {loading && (
        <div className="w-full px-[18px] pb-3 bg-[#FAFAF8]">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-[#6E1A36] h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Evenement;
